/**
 * Collect detailed errors for a single VIN candidate (no auto-mapping).
 *
 * @param {string} rawUpper
 * @returns {import('src/types/vin').VinError[]}
 */
export function collectErrorsForCandidate(rawUpper) {
  /** @type {import('src/types/vin').VinError[]} */
  const errors = [];

  // Length
  if (rawUpper.length !== 17) {
    errors.push({
      code: 'length',
      message: `Vehicle VINs are typically 17 characters, entered ${rawUpper.length}.`,
    });
  }

  // Forbidden letters & invalid characters
  const forbiddenIdx = [];
  const forbiddenChars = [];
  const invalidIdx = [];
  const invalidChars = [];

  const isAZ09 = /^[A-Z0-9]$/;
  for (let i = 0; i < rawUpper.length; i++) {
    const ch = rawUpper[i];
    if (!isAZ09.test(ch)) {
      invalidIdx.push(i);
      invalidChars.push(ch);
    } else if (ch === 'I' || ch === 'O' || ch === 'Q') {
      forbiddenIdx.push(i);
      forbiddenChars.push(ch);
    }
  }

  if (forbiddenIdx.length) {
    errors.push({
      code: 'forbidden_letters',
      message: `Forbidden letters present (I, O, or Q) at positions: ${forbiddenIdx.map((i) => i + 1).join(', ')}.`,
      indices: forbiddenIdx,
      chars: forbiddenChars,
    });
  }

  if (invalidIdx.length) {
    errors.push({
      code: 'invalid_chars',
      message: `Invalid character(s) detected at positions: ${invalidIdx.map((i) => i + 1).join(', ')}.`,
      indices: invalidIdx,
      chars: invalidChars,
    });
  }

  return errors;
}

/**
 * Map forbidden letters inside the VIN:
 * - I/|/! → 1
 * - O/Q → 0
 *
 * @param {string} vin
 */
export function mapForbidden(vin) {
  return stripNonAlphaNumeric(vin.replace(/[I|!]/g, '1').replace(/[OQ]/g, '0'));
}

/**
 * Performs minor normalization by:
 * - stripping white space and punctuation
 * - converting all characters to uppercase
 *
 * @param {string} vin
 */
export function minorNormalization(vin) {
  return (vin || '').toUpperCase().replace(/\s/g, '');
}

/**
 * Removes barcode sentinels ("I") at start/end when length is 18.
 *
 * @param {string} vin
 */
export function removeBarcodeSentinels(vin) {
  // Barcode sentinels: some scanners output "I" for start/stop (length often 18)
  if (vin.length >= 18 && vin.startsWith('I')) {
    vin = vin.slice(1);
  }
  if (vin.length >= 18 && vin.endsWith('I')) {
    vin = vin.slice(0, -1);
  }
  return vin;
}

/**
 * Performs minor normalization by:
 * - stripping white space and punctuation
 * - converting all characters to uppercase
 *
 * @param {string} vin
 */
export function stripNonAlphaNumeric(vin) {
  return vin.replace(/\W/g, '').replace(/_/g, '');
}

/**
 * Single-call entrypoint for user input:
 * - validates as typed
 * - if invalid, normalizes (barcode sentinels + I/O/Q mapping) and tries again
 * - returns final VIN, validity, reasons, and a suggestedVin if normalization fixed it
 *
 * @param {string} raw
 * @returns {import('src/types/vin').VinValidationResult}
 */
export function validateVin(vin) {
  if (vin.length < 17) {
    // If we don't have at least 17 characters, there's no reason to validate any further
    return {
      valid: false,
      vin: vin,
      attemptedNormalization: false,
      wasNormalized: false,
      errors: [{ code: 'length', message: `Vehicle VINs are typically 17 characters, entered ${vin.length}.` }],
      rawErrors: [],
      normalizedErrors: undefined,
    };
  }

  // Phase 1: raw attempt (no mapping)
  const rawErrors = collectErrorsForCandidate(vin);
  if (rawErrors.length === 0) {
    return {
      valid: true,
      vin: vin,
      attemptedNormalization: false,
      wasNormalized: false,
      errors: [],
    };
  }

  // Phase 2: normalize & re-validate
  const normalized = mapForbidden(vin);
  const wasNormalized = normalized !== vin;
  const normalizedErrors = collectErrorsForCandidate(normalized);
  if (wasNormalized && normalizedErrors.length === 0) {
    // Great: the normalized form is valid → suggest it and mark fix
    return {
      valid: true,
      vin: normalized,
      attemptedNormalization: true,
      wasNormalized,
      errors: [],
      rawErrors,
    };
  }

  // Still invalid → report final attempt’s errors
  return {
    valid: false,
    vin: wasNormalized ? normalized : vin,
    attemptedNormalization: true,
    wasNormalized,
    errors: normalizedErrors,
    rawErrors,
  };
}
